import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import HeaderInfo from "../components/common/HeaderInfo";
import DataTable from "../components/common/DataTable";
import CampusRequestModal from "../components/modals/CampusRequestModal";
import VPApproveModal from "../components/modals/VPApproveModal";
import RequestDetailsModal from "../components/modals/RequestDetailsModal";
import { Check, X as XIcon, Eye } from "lucide-react";
import {
  useGetCampusRequestsQuery,
  useCreateRequestMutation,
  useUpdateCampusRequestMutation,
  useVpApprovalRequestMutation,
  useGetMeQuery,
  useMarkRequestReceivedMutation,
  useGetIsCampusVpQuery,
  useGetIsStoreInchargeQuery,
} from "../store/api";

export default function CampusRequests() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [vpApproveOpen, setVpApproveOpen] = useState(false);
  const [approveRow, setApproveRow] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const { data: userData } = useGetMeQuery();
  const currentUserCampusId = userData?.data?.campus?._id || userData?.data?.campus || "";

  const { data: vpStatusData } = useGetIsCampusVpQuery();
  const { data: storeInchargeStatusData } = useGetIsStoreInchargeQuery();

  const isCampusVp = vpStatusData?.data?.isCampusVp || false;
  const isStoreIncharge = storeInchargeStatusData?.data?.isStoreIncharge || false;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const { data, isLoading, isFetching, error } = useGetCampusRequestsQuery({
    page,
    search: debouncedSearch,
  });

  const [createRequest, { isLoading: isCreating }] = useCreateRequestMutation();
  const [updateRequest, { isLoading: isUpdating }] = useUpdateCampusRequestMutation();
  const [vpApprovalRequest, { isLoading: isVpApproving }] = useVpApprovalRequestMutation();
  const [markRequestReceived, { isLoading: isMarkingReceived }] = useMarkRequestReceivedMutation();

  useEffect(() => {
    if (error) {
      toast.error(error?.data?.message || "Failed to load requests");
    }
  }, [error]);

  const requests = data?.data?.requests || [];

  const backendTotalPages = data?.data?.totalPages || data?.pagination?.totalPages;
  let totalPages = backendTotalPages || 1;

  function handleCloseEdit() {
    setEditOpen(false);
    setEditRow(null);
  }



  function handleNext() {
    if (page < totalPages) setPage((p) => p + 1);
  }

  function handlePrev() {
    if (page > 1) setPage((p) => p - 1);
  }

  const handleMarkReceived = async (r) => {
    try {
      const response = await markRequestReceived({ requestId: r._id }).unwrap();
      if (response?.success === false) {
        toast.error(response?.message || "Failed to mark as received");
        return;
      }
      toast.success("Items received successfully");
    } catch (error) {
      console.error(error);
      toast.error(error?.data?.message || "Failed to mark as received");
    }
  };

  const handleReject = async (r) => {
    try {
      const response = await vpApprovalRequest({
        requestId: r._id,
        status: "rejected_vp",
        modifiedQuantity: r.requestedQty,
      }).unwrap();
      if (response?.success === false) {
        toast.error(response?.message || "Failed to reject request");
        return;
      }
      toast.success("Request rejected successfully");
    } catch (error) {
      console.error(error);
      toast.error(error?.data?.message || "Failed to reject request");
    }
  };

  const handleApprove = (r) => {
    setApproveRow(r);
    setVpApproveOpen(true);
  };

  const rows = requests.map((item) => ({
    ...item,
    id: item._id,
  }));

  const renderRow = (r, idx) => (
    <div key={idx} className="bg-[#EEEEEE40]">
      <div className="grid grid-cols-12 gap-4 px-7 py-6 items-center border-b border-[#E5E5E5]">
        <div className="col-span-3 flex items-center gap-3">
          <div className="w-[37px] h-[37px] flex items-center justify-center rounded-full bg-[#6D4E8A] shrink-0 overflow-hidden">
            {r?.item?.itemPicture?.secure_url ? (
              <img src={r.item.itemPicture.secure_url} alt="Item" className="w-full h-full object-cover" />
            ) : (
              <p className="text-white text-xs">{r?.item?.name?.charAt(0) || "I"}</p>
            )}
          </div>
          <div>
            <div className="text-black font-medium leading-5">{r?.item?.name || "Unknown Item"}</div>
            <div className="text-xs text-[#878686]">{r?.requestedBy?.name || "Unknown User"}</div>
          </div>
        </div>

        <div className="col-span-2 text-center">
          <div className="text-black leading-8">{r.requestedQty} Requested</div>
          {r.issuedQty > 0 && <p className="text-[#878686] text-xs leading-3">{r.issuedQty} Issued</p>}
        </div>

        <div className="col-span-3 flex justify-center">
          <div
            className={`mt-1 rounded-[10px] text-xs px-4 py-1 inline-block ${r.status === "pending_vp" || r.status === "pending_central"
              ? "bg-[#F6E8AD] text-[#986012]"
              : r.status === "approved_central"
                ? "bg-[#E5FFF6] text-[#2C765C]"
                : r.status === "recieved"
                  ? "bg-[#D6EAFE] text-[#0052CC]"
                  : r.status === "rejected_vp" || r.status === "rejected_central"
                    ? "bg-[#FFBFBF] text-[#F6200D]"
                    : "bg-[#EAEAEA] text-[#7A7A7A]"
              }`}
          >
            {r.status === "pending_vp"
              ? "Pending VP"
              : r.status === "pending_central"
                ? "Pending Central"
                : r.status === "approved_central"
                  ? "Approved / Dispatch"
                  : r.status === "recieved"
                    ? "Received"
                    : r.status === "rejected_vp"
                      ? "Rejected (VP)"
                      : r.status === "rejected_central"
                        ? "Rejected (Central)"
                        : r.status || "N/A"}
          </div>
        </div>

        <div className="col-span-2 text-center text-xs">
          <p className="text-black leading-tight">
            {new Intl.DateTimeFormat("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            }).format(new Date(r.createdAt))}
          </p>
          <p className="text-[#878686] mt-1">
            {new Date(r.createdAt).toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })}
          </p>
        </div>

        <div className="col-span-2 flex justify-end items-center gap-2">
          {r.status === "approved_central" && isStoreIncharge && (
            <button
              onClick={() => handleMarkReceived(r)}
              disabled={isMarkingReceived}
              title="Mark Received"
              className="shrink-0 px-3 py-1 rounded-[10px] bg-[#E5FFF6] text-[#2C765C] hover:bg-[#2C765C] hover:text-white border border-[#2C765C] transition text-sm font-medium disabled:opacity-50"
            >
              Mark Received
            </button>
          )}
          {r.status === "pending_vp" && isCampusVp && (
            <>
              <button
                onClick={() => handleApprove(r)}
                title="Approve"
                className="shrink-0 w-[30px] h-[30px] bg-[#E5FFF6] rounded-full flex items-center justify-center border border-[#2C765C] text-[#2C765C] hover:bg-[#2C765C] hover:text-white transition-colors"
              >
                <Check size={14} strokeWidth={3} />
              </button>
              <button
                onClick={() => handleReject(r)}
                title="Reject"
                className="shrink-0 w-[30px] h-[30px] bg-[#FFBFBF] rounded-full flex items-center justify-center border border-[#F6200D] text-[#F6200D] hover:bg-[#F6200D] hover:text-white transition-colors"
              >
                <XIcon size={14} strokeWidth={3} />
              </button>
            </>
          )}

          <button
            onClick={() => {
              setSelectedRequest({
                ...r,
                item: r?.item || r?.centralCampusInventoryItemId?.itemId
              });
              setDetailsOpen(true);
            }}
            className="shrink-0 w-[30px] h-[30px] bg-[#EAEAEA] rounded-full flex items-center justify-center border border-[#E5E5E5] text-[#7A7A7A] hover:text-[#1FA5DE]"
            title="View Details"
          >
            <Eye size={14} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="pt-10 lg:pt-14 px-6 xl:pl-8 xl:pr-12 min-h-screen">
      <HeaderInfo
        title="Campus Requests"
        desc="Manage campus requests and their details"
        btnLabel="Add New"
        onAdd={() => setAddOpen(true)}
        isSearchBar={true}
        searchPlaceholder="Search Requests"
        searchValue={search}
        onSearchChange={setSearch}
        showFilterButton={false}
      />

      <div className="mt-4 lg:mt-6 lg:ml-2.5">
        <DataTable
          columns={[
            { key: "requested", label: "Item Requested", className: "col-span-3 pl-7" },
            { key: "quantity", label: "Quantity", className: "col-span-2 text-center" },
            { key: "status", label: "Status", className: "col-span-3 text-center" },
            { key: "timeline", label: "Timeline", className: "col-span-2 text-center" },
            { key: "actions", label: "Actions", className: "col-span-2 text-right pr-7" },
          ]}
          rows={rows}
          renderRow={renderRow}
          page={page}
          totalPages={totalPages}
          onNext={handleNext}
          onPrev={handlePrev}
          isLoading={isLoading || isFetching}
        />
      </div>

      <CampusRequestModal
        open={addOpen}
        mode="add"
        isLoading={isCreating}
        onClose={() => setAddOpen(false)}
        onSubmit={async (payload) => {
          try {
            const finalPayload = {
              ...payload,
              requestedCampusId: currentUserCampusId,
            };
            const response = await createRequest(finalPayload).unwrap();
            if (response?.success === false) {
              toast.error(response?.message || "Failed to create request");
              return;
            }
            toast.success("Request created successfully");
            setAddOpen(false);
          } catch (error) {
            console.error(error);
            toast.error(error?.data?.message || "Failed to create request");
          }
        }}
      />

      <CampusRequestModal
        open={editOpen}
        mode="edit"
        isLoading={isUpdating}
        onClose={handleCloseEdit}
        initialValue={{
          centralCampusInventoryItemId: editRow?.centralCampusInventoryItemId?._id || editRow?.centralCampusInventoryItemId || "",
          requestedQty: editRow?.requestedQty || "",
        }}
        onSubmit={async (payload) => {
          try {
            const response = await updateRequest({
              id: editRow.id,
              requestedCampusId: currentUserCampusId,
              ...payload,
            }).unwrap();
            if (response?.success === false) {
              toast.error(response?.message || "Failed to update request");
              return;
            }
            toast.success("Request updated successfully");
            handleCloseEdit();
          } catch (error) {
            console.error(error);
            toast.error(error?.data?.message || "Failed to update request");
          }
        }}
      />

      <VPApproveModal
        open={vpApproveOpen}
        onClose={() => setVpApproveOpen(false)}
        requestData={approveRow}
        isLoading={isVpApproving}
        onSubmit={async (qty) => {
          try {
            const response = await vpApprovalRequest({
              requestId: approveRow._id,
              status: "pending_central",
              modifiedQuantity: qty,
            }).unwrap();
            if (response?.success === false) {
              toast.error(response?.message || "Failed to approve request");
              return;
            }
            toast.success("Request approved and sent to Central successfully");
            setVpApproveOpen(false);
          } catch (error) {
            console.error(error);
            toast.error(error?.data?.message || "Failed to approve request");
          }
        }}
      />

      <RequestDetailsModal
        open={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          setSelectedRequest(null);
        }}
        requestData={selectedRequest || {}}
      />
    </div>
  );
}
